import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { OneOnOneColleagueItem } from '@/lib/colleagues.ts';
import { patchColleague } from './patchColleague.ts';
import {
  addFollowException,
  fetchFollowExceptions,
  fetchFollowPreferences,
  removeFollowException,
} from '@/lib/meetingFollow.ts';
import { followCorrectionsFor } from '@/lib/personCorrections.ts';

export interface SupportChange {
  colleague: OneOnOneColleagueItem;
  supported: boolean;
}

/**
 * The switch that says whether Wundo supports the 1:1s with a colleague — the
 * same person follow as an onboarding pick. One mutation for the Team page and
 * a colleague's page, so both show the choice the moment it is made.
 */
export function useOneOnOneSupport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ colleague, supported }: SupportChange) => {
      /*
       * Off has to mean off. With every 1:1 followed, only an explicit "don't
       * follow" for this person stops it; otherwise dropping the pick is enough.
       */
      const preferences = supported ? null : await fetchFollowPreferences();
      if (supported || preferences?.follow_all || preferences?.follow_one_on_ones) {
        await addFollowException({
          target_kind: 'person',
          email: colleague.email,
          follow: supported,
          record: null,
        });
        return;
      }

      for (const correction of followCorrectionsFor(
        colleague,
        await fetchFollowExceptions(),
      )) {
        await removeFollowException(correction.id);
      }
    },
    onMutate: ({ colleague, supported }) =>
      patchColleague(queryClient, colleague.id, { supported }),
    /* Settled rather than successful: the re-read is what reverts a switch the server refused. */
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['oneOnOneColleagues'] });
      await queryClient.invalidateQueries({ queryKey: ['oneOnOneColleague'] });
      await queryClient.invalidateQueries({ queryKey: ['meetingFollowExceptions'] });
      await queryClient.invalidateQueries({ queryKey: ['meetings'] });
    },
  });
}
